import React from 'react';
import {
  IconShield,
  IconStar,
  IconSettings,
  IconBan,
  IconLockOpen
} from '@tabler/icons-react';

const UsersTable = ({ users, onEditPlan, onEditLimit, onToggleAdmin, onBan, onUnban }) => {
  const getAdminLevelLabel = (level) => {
    switch (level) {
      case 1: return 'Модератор';
      case 2: return 'Админ';
      case 3: return 'Главный';
      default: return 'Пользователь';
    }
  };

  const getAdminLevelColor = (level) => {
    switch (level) {
      case 1: return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
      case 2: return 'bg-purple-500/10 text-purple-400 border-purple-500/30';
      case 3: return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/30';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white dark:bg-dark-900 rounded-2xl border border-gray-200 dark:border-dark-800 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-dark-850">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-dark-500 uppercase">Пользователь</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-dark-500 uppercase">План</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-dark-500 uppercase">Туннели</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-dark-500 uppercase">Лимит</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-dark-500 uppercase">Статус</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-dark-500 uppercase">Регистрация</th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-dark-500 uppercase">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-dark-800">
            {users.map(u => (
              <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-dark-850 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500/20 to-primary-500/5 flex items-center justify-center">
                      <span className="text-primary-500 font-semibold">{u.username?.[0]?.toUpperCase()}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">{u.username}</span>
                        {u.admin_level > 0 && (
                          <span className={`px-2 py-0.5 text-xs rounded-full border ${getAdminLevelColor(u.admin_level)}`}>
                            {getAdminLevelLabel(u.admin_level)}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 dark:text-dark-600">{u.id?.slice(0, 8)}...</span>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    u.plan === 'paid' 
                      ? 'bg-yellow-500/20 text-yellow-500' 
                      : 'bg-gray-500/20 text-gray-500 dark:text-dark-400'
                  }`}>
                    {u.plan === 'paid' && <IconStar className="w-3 h-3" />}
                    {u.plan === 'paid' ? 'PRO' : 'Free'}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-900 dark:text-white">{u.tunnel_count}</td>
                <td className="px-6 py-4">
                  <span className="text-gray-900 dark:text-white">
                    {u.tunnel_limit > 0 ? u.tunnel_limit : (u.plan === 'paid' ? 20 : 10)}
                  </span>
                  {u.tunnel_limit > 0 && (
                    <span className="text-xs text-primary-500 ml-1">(custom)</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {u.is_banned ? (
                    <div>
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-500 text-xs rounded-full">
                        <IconBan className="w-3 h-3" />
                        Забанен
                      </span>
                      {u.banned_until && (
                        <div className="text-xs text-gray-500 dark:text-dark-600 mt-1">
                          до {formatDate(u.banned_until)}
                        </div>
                      )}
                      {u.ban_reason && (
                        <div className="text-xs text-gray-500 dark:text-dark-600 mt-1">
                          {u.ban_reason}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-500 text-xs rounded-full">
                      Активен
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 dark:text-dark-400">
                  {formatDate(u.created_at)}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEditPlan(u)}
                      className={`p-2 rounded-lg transition-colors ${
                        u.plan === 'paid'
                          ? 'text-yellow-500 hover:bg-yellow-500/20'
                          : 'text-gray-600 dark:text-dark-400 hover:text-yellow-500 hover:bg-gray-100 dark:hover:bg-dark-800'
                      }`}
                      title="Изменить план"
                    >
                      <IconStar className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onEditLimit(u)}
                      className="p-2 text-gray-600 dark:text-dark-400 hover:text-primary-500 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg transition-colors"
                      title="Изменить лимит"
                    >
                      <IconSettings className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onToggleAdmin(u.id, u.admin_level || 0)}
                      className={`p-2 rounded-lg transition-colors ${
                        u.admin_level > 0 
                          ? 'text-primary-500 hover:bg-primary-500/20' 
                          : 'text-gray-600 dark:text-dark-400 hover:text-primary-500 hover:bg-gray-100 dark:hover:bg-dark-800'
                      }`}
                      title={`Уровень: ${getAdminLevelLabel(u.admin_level || 0)} → ${getAdminLevelLabel(((u.admin_level || 0) + 1) % 4)}`}
                    >
                      <IconShield className="w-4 h-4" />
                    </button>
                    {u.is_banned ? (
                      <button
                        onClick={() => onUnban(u.id)}
                        className="p-2 text-green-500 hover:bg-green-500/20 rounded-lg transition-colors"
                        title="Разбанить"
                      >
                        <IconLockOpen className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => onBan(u)}
                        className="p-2 text-red-500 hover:bg-red-500/20 rounded-lg transition-colors"
                        title="Забанить"
                      >
                        <IconBan className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersTable;